import Player from './Player';
import Enemy from './Enemy';
import Companion from './CompanionEntity';
import Deck from './Deck';
import Hand from './Hand';
import Court from './Court';
import Supply from './Supply';
import Discard from './Discard';
import Exile from './Exile';
import Card from './Card';
import Library from '../utility/Library';

const BASE_MANA = 3;
const MANA_PER_FlOOR = 0;
const HAND_SIZE = 6;
const SLIME_BODY_DRAW_CHANCE = 0.1;

export default class Fight {

	data = {};
	ids = {};
	variables = {};

	mana = 0;
	turncount = 0;
	enginesPlayed = 0;

	constructor (run) {

		this.run = run;
		this.enemies = [];
		this.slots = [];
		this.loot = [];
		this.player = new Player(this);
		this.turnmana = BASE_MANA + MANA_PER_FlOOR * (this.run.floor-1);
		if (this.player.champion.companion)
			this.companion = new Companion(this);
		this.deck = new Deck(this);
		this.hand = new Hand(this);
		this.court = new Court(this);
		this.supply = new Supply(this);
		this.discard = new Discard(this);
		this.exile = new Exile(this);
		this.notify('newfight', this, run);
	}

	get activeEnemies () {

		return this.enemies.filter(enemy => enemy.alive || enemy.passive);
	}

	get liveEnemies () {

		return this.enemies.filter(enemy => enemy.alive);
	}

	get requiredEnemies () {

		return this.liveEnemies.filter(enemy => !enemy.hasStatus("minion"));
	}

	start (slots) {

		this.done = false;
		this.overdecksize = Math.max(0, this.run.deckSize - this.run.minDeckSize);
		this.player.init();
		this.slots = this.normalizeSlots(slots);
		this.enemies = [];
		this.slots.forEach((slot, index) => {
			if (!slot.enemy)
				return;
			this.addEnemyToSlot(slot.enemy, index);
		});
		this.deck.init(this.run.deck.filter(card => card.status !== "disabled").map(card => card.card));
		this.enemies.forEach(enemy => enemy.init());
		this.notify('startfight', this);
		this.playerTurn();
	}

	normalizeSlots (slots) {

		if (!slots)
			return [];
		return slots.map(slot => {
			slot = typeof slot === "string" ? { enemy: slot } : Object.assign({}, slot);
			if (slot.scale === undefined)
				slot.scale = slot.enemy ? this.enemyModelScale(slot.enemy) : 1;
			return slot;
		});
	}

	enemyModelScale (model) {

		if (!model)
			return 1;
		if (typeof model === "string")
			return Library.getUpgradedEnemy(model, this.run.difficultySetting)?.scale || 1;
		return model.model?.scale || model.scale || 1;
	}

	slotScale (slot) {

		if (slot.scale !== undefined)
			return slot.scale;
		if (slot.enemy)
			return this.enemyModelScale(slot.enemy);
		return 1;
	}

	addEnemyToSlot (model, index) {

		let enemy = new Enemy(this, model, index);
		this.enemies.push(enemy);
		let slot = Object.assign({}, this.slots[index], { enemy });
		if (slot.scale === undefined)
			slot.scale = this.enemyModelScale(enemy);
		this.slots[index] = slot;
		return enemy;
	}

	removeEnemy (enemy) {

		if (!enemy)
			return;

		this.enemies = this.enemies.filter(e => e !== enemy);
		this.slots = this.slots.map(slot => slot.enemy === enemy ? Object.assign({}, slot, { enemy: undefined }) : slot);

		if (this.data.enemy && enemy.id)
			delete this.data.enemy[enemy.id.no];
		if (this.data.enemy && Object.keys(this.data.enemy).length === 0)
			delete this.data.enemy;

		enemy.statuses.slice().forEach(status => status.dissipate(true));
	}

	removeMinions () {

		let minions = this.liveEnemies.filter(enemy => enemy.hasStatus("minion"));
		if (minions.length === 0)
			return;
		minions.forEach(enemy => this.removeEnemy(enemy));
		this.notify('minionsdie', minions);
	}

	availableSpawnSlot (preferredSlot) {

		if (preferredSlot !== undefined && this.slots[preferredSlot] && !this.slots[preferredSlot].enemy && this.slots[preferredSlot].spawn !== false)
			return preferredSlot;

		return this.slots.findIndex(slot => !slot.enemy && slot.spawn !== false);
	}

	spawnEnemy (model, preferredSlot) {

		const index = this.availableSpawnSlot(preferredSlot);
		if (index === -1)
			return null;
		const enemy = this.addEnemyToSlot(model, index);
		enemy.init();
		return enemy;
	}

	playerTurn (isExtraTurn=false) {

		this.turn = "player";
		this.turncount++;
		this.enginesPlayed = 0;
		this.mana = (this.player.hasStatus("wake-up") || this.player.hasStatus("life-loop") ? this.mana : (this.player.hasStatus("natures-gift") || this.player.hasStatus("mana-loop") ? Math.min(this.mana, this.player.getStatusValue("natures-gift") + this.player.getStatusValue("mana-loop")) : 0)) + this.turnmana;
		this.mana = Math.min(this.mana, 100);

		if (!isExtraTurn)
			this.enemies.forEach(enemy => enemy.haveIntent());
		/*if (this.companion)
			this.companion.haveIntent();*/

		let handSize = HAND_SIZE;
		if (this.player.statuses)
			this.player.statuses.forEach(status => {
				if (!status.model.modifiers)
					return;
				status.model.modifiers.filter(modifier => modifier.stat === "handsize").forEach(modifier => handSize += modifier.value * status.value);
			})
		handSize = Math.min(Math.max(0, handSize), handSize);
		
		this.deck.shuffle();
		this.player.update();
		this.notify('playerturn', this);

		if (this.hand.count < handSize)
			this.draw(handSize - this.hand.count);
	}

	endPlayerTurn () {

		if (this.choosingHand)
			return;
		const timewarp = this.player.hasStatus("timewarp");
		this.notify('endplayerturn', this);
		this.player.endturnUpdate();
		this.turn = "cleanup";
		this.hand.cards.filter(card => card.volatile || card.overloaded).forEach(card => card.discard());
		this.clearBurningCards();
		this.hand.cards.forEach(card => {
			if (card.manashift)
				delete card.manashift;
			if (card.playBlocked)
				card.removeProperty("playBlocked");
		});
		this.discard.cards.forEach(card => card.goto(this.supply));
		if (this.companion)
			this.companion.act();
		if (!this.done) {
			if (timewarp)
				this.playerTurn(true);
			else
				this.enemyTurn();
		}
	}

	clearBurningCards () {

		this.hand.cards.filter(card => card.burning).forEach(card => card.removeProperty("burning"));
	}

	enemyTurn () {

		this.turn = "enemy";
		this.activeEnemies.forEach(enemy => enemy.update());
		this.notify('enemyturn', this);
		this.activeEnemies.forEach(enemy => enemy.act());
		this.endEnemyTurn();
	}

	endEnemyTurn () {

		this.activeEnemies.forEach(enemy => enemy.endturnUpdate());
		this.notify('endenemyturn', this);
		if (!this.done)
			this.playerTurn();
	}

	draw (n=1, filter) {

		if (this.hand.isFull)
			return;
		let card;
		/*if (!filter && this.run.champion.model.key === 3 && !this.hand.cards.some(card => card.engine) && (this.deck.cards.find(card => card.engine) || this.supply.cards.find(card => card.engine))) {
			this.draw(1, card => card.engine);
			n--;
			if (!n)
				return;
		}*/
		/*if (!filter && this.run.champion.model.key === 5) {
			const turnProperty = this.player.hasStatus("facing-back") ? "turnForward" : "turnBack";
			const needsTurnCard = !this.hand.cards.some(card => card.model[turnProperty]);
			const hasTurnCardToDraw = this.deck.cards.some(card => card.model[turnProperty]) || this.supply.cards.some(card => card.model[turnProperty]);
			if (needsTurnCard && hasTurnCardToDraw) {
				this.draw(1, card => card.model[turnProperty]);
				n--;
				if (!n)
					return;
			}
		}*/
		if (filter) {
			card = this.deck.draw(filter);
			if (!card) 
				card = this.supply.cards.find(filter);
		} else {
			if ((!this.deck.isEmpty || !this.supply.isEmpty) && this.run.champion.curses?.some(curse => curse.key === "slime-body") && Card.canGenerate(this, "slime") && Math.random() < SLIME_BODY_DRAW_CHANCE)
				card = new Card(this, "slime");
			else {
				if (this.deck.isEmpty && !this.supply.isEmpty) {
					this.supply.cards.forEach(card => card.goto(this.deck));
					if (this.run.champion.getPassive("shuffleblock"))
						this.player.gainblock(this.run.champion.getPassive("shuffleblock"));
					if (this.run.champion.getPassive("shufflescreen"))
						this.player.apply("milkscreen", this.run.champion.getPassive("shufflescreen"));
					this.draw(n, null);
					return;
				}
				card = this.deck.draw();
			}
		}
		if (!card)
			return;
		if (this.hand.isFull) {
			//this.game.notify("burn.before", this, card);
			//card.banish();
			//card.discard();
			//this.game.notify("burn", this, card);
		}
		else {
			this.run.notify("draw.before", this, card);
			this.hand.addCard(card);
			if (card.autocast) {
				card.play(null, false, true);
				this.draw();
			}
			this.run.notify("draw", this, card);
		}
		if (n > 1)
			this.draw(n-1, filter);
		else return card;
	}

	addMana (mana) {

		this.mana += mana;
		this.notify('addmana', this, mana);
	}

	pay (mana, engine=false, manaOnly=false) {

		const paid = Math.min(mana, this.mana);
		this.mana -= paid;
		if (engine)
			this.enginesPlayed++;

		this.notify('paymana', this, paid, engine, manaOnly);
	}

	generateGoldReward (depth) {

		let gold = (4 + depth) * 1.5;
		gold += Math.floor(gold * 0.3 * Math.random());
		gold += Math.floor(Math.random() * 4);
		return gold;
	}

	lootRelic (difficulty) {

		let relics = Library.relics;
		relics = Object.values(relics).filter(relic => {
			switch (difficulty) {
			case "boss":
				if (relic.rarity !== "boss")
					return false;
				break;
			case "elite":
				if (relic.rarity !== "common")
					return false;
				break;
			}
			if (relic.champion && relic.champion !== this.run.champion.model.key)
				return false;
			if (relic.floors && (this.run.depth < relic.floors[0] || this.run.depth > relic.floors[1]))
				return false;
			if (this.run.champion.relics.some(r => r.key === relic.key))
				return false;
			return true;
		});

		let relic = relics[Math.floor(Math.random() * relics.length)];
		return Library.upgradeItem(relic, this.run.power);
	}

	runAway () {

		if (this.done || this.run.room.type === "boss")
			return;

		this.done = true;
		this.notify("winfight", this);
		this.healAfterFight();

		this.cleanup();
	}

	win () {

		if (this.done)
			return;

		this.done = true;
		if (this.run.storyEvent) {
			this.healAfterFight();
			this.run.victory();
			return;
		}

		this.removeMinions();
		this.notify("winfight", this);

		this.healAfterFight();

		let powermult = Math.pow(this.run.power, 2);
		let boss = this.run.room.model.type === "boss";
		let lastFight = boss && this.run.floor === this.run.maxFloor;
		let elite = this.run.room.model.difficulty === 3;
		let exp = Math.ceil((boss ? this.run.floor * 30 : (elite ? this.run.floor * 10 + 5 : this.run.floor * 10)) * powermult * this.run.expMultiplier);
		let gold = Math.floor((this.run.floor+1)/2 * (1 + Math.random()) * (boss ? 10 : (elite ? 5 : 4)));
		let health = 0;
		let paws = false;
		let key = false;
		//let insight = 0;
		let card = false;
		let potions = [];

		let rewards;
		if (Array.isArray(this.run.room.reward))
			rewards = this.run.room.reward;
		else rewards = [this.run.room.reward];

		if (boss && !lastFight)
			rewards.push("relic");

		rewards.forEach(reward => {

			switch (reward) {
			//case "insight": insight += 2; break;
			case "card": card = true; break;
			case "paws": paws = true; break;
			case "key": key = true; break;
			case "relic": {
				let relics = Object.values(Library.relics);
				relics = relics.filter(relic => relic.pool === "treasure" && (!relic.champion || relic.champion === this.run.champion.model.key))
				relics = relics.filter(relic => !this.run.champion.relics.some(r => r.key === relic.key))

				const weightedArray = relics.flatMap(item => {
			    	let weight = 1;
			        return Array(weight).fill(item);
			    });

				let relic = Library.upgradeItem(weightedArray[Math.floor(Math.random() * weightedArray.length)], this.run.power);
				this.run.room.addCollectable("relic", relic);
				break;
			}
			default: break;
			}
		})

		// Adjust rewards for passives
		if (this.run.champion.getPassive("fightrewardexp"))
		    exp += this.run.champion.getPassive("fightrewardexp") * this.run.floor;
		if (this.run.champion.getPassive("overdecksizeexp"))
		    exp += this.run.champion.getPassive("overdecksizeexp") * this.run.floor * Math.min(10, this.overdecksize || 0);
		if (elite && this.run.champion.getPassive("hardfightrewardexp"))
		    exp += this.run.champion.getPassive("hardfightrewardexp") * this.run.floor;
		if (this.run.champion.getPassive("fightrewardgold"))
		    gold += this.run.champion.getPassive("fightrewardgold");
		if (this.run.champion.getPassive("overdecksizegold"))
		    gold += this.run.champion.getPassive("overdecksizegold") * Math.min(10, this.overdecksize);
		if (this.run.champion.getPassive("goldrewardmultiplier"))
		    gold = Math.round(gold * (1 + this.run.champion.getPassive("goldrewardmultiplier")));
		if (this.run.champion.getPassive("fightrewardhealth"))
		    health += this.run.champion.getPassive("fightrewardhealth");
		if (this.run.champion.getPassive("endfightclimaxhp") && this.getVariable("lastcardclimax") === 1)
		    health += this.run.champion.getPassive("endfightclimaxhp");
		if (this.run.champion.getPassive("noskillhp") && this.run.companion && !this.player.spentSkill)
		    health += this.run.champion.getPassive("noskillhp");
		/*if (insight && this.run.champion.getPassive("extrainsightreward"))
		    insight += this.run.champion.getPassive("extrainsightreward");*/
		if (elite || boss)
			this.run.pityTimerPotion++;
		if (!lastFight && (Math.random() < 0.25 + this.run.pityTimerPotion * 0.1 || this.run.champion.getPassive("potiondrop"))) {
			let itempool = Object.values(Library.potions)
	            .filter(item =>
	                item.rarity && (!item.rank || this.run.power >= item.rank) &&
	                (!item.champion || item.champion === this.run.champion.model.key || (Array.isArray(item.champion) && item.champion.includes(this.run.champion.model.key)))
	            );
	        let chosenItem = null;
	        while (!chosenItem) {
	        	chosenItem = itempool[Math.floor(Math.random() * itempool.length)];
	        	if (chosenItem.rarity === 2 && Math.random() < 0.66 || chosenItem.rarity === 3 && Math.random() < 0.83)
	        		chosenItem = null;
	        }
			potions.push(Library.upgradeItem(chosenItem, this.run.power));
			this.run.pityTimerPotion -= 2;
		} else
			this.run.pityTimerPotion++;
		if (elite && this.run.champion.getPassive("hardfightmaxhp"))
		    this.run.champion.gainMaxHp(this.run.champion.getPassive("hardfightmaxhp"));

		// Add rewards to the room
		if (exp)
		    this.run.room.addCollectable("exp", exp);
		if (this.stolenExp)
		    this.run.room.addCollectable("bottledexp", this.stolenExp);
		if (!lastFight && gold)
		    this.run.room.addCollectable("gold", gold);
		if (!lastFight && health)
		    this.run.room.addCollectable("health", health);
		if (paws)
		    this.run.room.addCollectable("paws");
		if (key)
		    this.run.room.addCollectable("smallkey");
		/*if (insight)
		    this.run.room.addCollectable("insight", insight);*/

		if (!lastFight) {
			
			potions.forEach(item => this.run.room.addCollectable("potion", item))

			rewards.forEach(reward => {

				switch (reward) {
				case "remove": this.run.room.addCollectable("remove"); break;
				case "coin": this.run.room.addCollectable("coin"); break;
				case "berry": this.run.room.addCollectable("berry"); break;
				case "seed": this.run.room.addCollectable("seed"); break;
				default: break;
				}
			})

			if (boss) {
				this.run.room.addCollectable("berry");
			} else {
				this.run.room.addCollectable("card", this.run.generateCardChoices(1)[0]);
				if (card)
					this.run.room.addCollectable("cardchoice", elite);
				/*if (this.run.insight > 0) {
					this.run.addInsight(-1);
					this.run.room.addCollectable("cardchoice", elite);
				}*/
			}
		}

		this.cleanup();
	}

	healAfterFight () {

		const champion = this.run.champion;
		const healing = champion.getPassive("regeneration");
		if (healing > 0 && champion.hp < champion.maxhp)
			champion.heal(healing);
	}

	lose () {

		if (this.done)
			return;

		this.cleanup();
		this.run.gameOver();
	}

	cleanup () {

		this.done = true;
		this.player?.passives?.forEach(p => p());
		if (this.data.card)
			Object.values(this.data.card).forEach(c => {
				if (c.passives)
					c.passives.forEach(p => p())
			});
		this.player.statuses.forEach(status => status.dissipate());
		this.enemies.forEach(enemy => enemy.statuses.forEach(status => status.dissipate()));
	}

	stealExp (exp) {

		let stolen = Math.min(exp, this.run.champion.availableExp);
		this.run.champion.loseExp(stolen);
		this.stolenExp = (this.stolenExp || 0) + stolen;
	}

	reward () {

		this.rewarding = true;
		this.notify('fightreward', this);
	}

	addLoot (loot) {

		let gold = this.loot.filter(l => l.type === "gold").length > 0 ? this.loot.filter(l => l.type === "gold")[0] : null;
		if (gold && loot.type === "gold")
			gold.value += loot.value;
		else this.loot.push(loot);
		this.notify('fightloot', this, loot);
	}

	collect (n) {

		let loot = this.loot[n];
		switch (loot.type) {
		case "card":
			this.run.cardChoice(null, true);
			break;
		case "gold":
			this.run.champion.gainGold(loot.value);
			break;
		case "exp":
			this.run.champion.gainExp(loot.value);
			break;
		case "relic":
			this.run.champion.newRelic(loot.relic);
			break;
		case "potion":
			this.run.champion.newPotion(loot.value);
			break;
		default: break;
		}

		this.loot.splice(n, 1);
		this.notify('collectloot', this, n);
	}

	cardChoice () {

		this.choosingCard = true;

		let cards = Object.values(Library.cards);
		cards = cards.filter(c => c.champion === this.run.champion.model.key && c.level > 0 && c.level > this.run.champion.level - 3 && c.level <= this.run.champion.level);
		let cardlevels = Array.from({ length: this.run.champion.level }, (_, i) => cards.filter(c => c.level === i + 1));
		let lv1 = cardlevels.length > 3 ? cardlevels[cardlevels.length-3] : cardlevels[0];
		let lv2 = cardlevels.length > 2 ? cardlevels[cardlevels.length-2] : cardlevels[0];
		let lv3 = cardlevels[cardlevels.length-1];
		let options = [];
		for (let i = 0; i < 3; i++) {
			let option = null;
			let r = Math.random();
			let lv = r < 0.4 ? lv3 : (r < 0.7333 ? lv2 : lv1);
			while (option === null || options.includes(option))
				option = lv[Math.floor(Math.random() * lv.length)];
			options.push(option);
		}

		this.options = options;
		this.notify('lootcardchoice', this, options);
	}

	collectCard (n) {

		if (!this.options || !this.choosingCard)
			return;

		this.run.addCard(this.options[n]);

		delete this.choosingCard;
		delete this.options;
		this.notify('collectcard', this, n);
	}

	handChoice (callback) {

		if (this.done)
			return;
		this.choosingHand = callback;
		this.notify('handchoice', this);
	}

	chooseHand (card) {

		if (this.choosingHand && this.hand.cards.includes(card)) {
			this.notify('chosehand', this);
			let ch = this.choosingHand;
			this.choosingHand(card);
			if (ch === this.choosingHand)
				delete this.choosingHand;
		}
	}

	setVariable (name, value) {

		this.variables[name] = value;
		this.notify('setfvariable', this, name, value);
		return;
	}

	getVariable (name) {

		return this.variables[name];
	}

	clearVariable (name) {

		this.variables[name] = undefined;
		this.notify('setfvariable', this, name, undefined);
		return;
	}

	notify (type, ...data) {

		if (this.run)
			this.run.notify(type, ...data);
	}

	subscribe (type, notify) {

		if (this.run)
			return this.run.subscribe(type, notify);
		return () => {};
	}

	register (item, type, givenno) {

		item.master = this;
		this.data[type] = this.data[type] || {};
		this.ids[type] = this.ids[type] || 0;
		let no = givenno === undefined ? this.ids[type]++ : givenno;
		if (givenno !== undefined)
			this.ids[type] = Math.max(this.ids[type], no + 1);
		item.id = {type, no};
		this.data[type][no] = item;
	}

	unregister (item) {

		let id = item.id;
		if (!id || !this.data[id.type] || !this.data[id.type][id.no])
			return;
		delete this.data[id.type][id.no];
		if (Object.keys(this.data[id.type]).length === 0)
			delete this.data[id.type];
		delete item.id;
	}

	find (id) {

		switch (id) {
		case "champion": return this.run.champion;
		case "player": return this.player;
		case "deck": return this.deck;
		case "hand": return this.hand;
		case "discard": return this.discard;
		case "supply": return this.supply;
		case "exile": return this.exile;
		default: return id && this.data[id.type] ? this.data[id.type][id.no] : undefined;
		}
	}

	serialize () {

		return {
			card: this.data.card ? Object.values(this.data.card).map(c => c.serialize()) : [],
			enemy: this.data.enemy ? Object.values(this.data.enemy).map(e => e.serialize()) : [],
			enemies: this.enemies.map(e => e.key),
			slots: this.slots.map(slot => ({
				spawn: slot.spawn,
				scale: slot.scale,
				enemy: slot.enemy ? slot.enemy.key : undefined
			})),
			player: this.player.serialize(),
			companion: this.companion?.serialize(),
			loot: this.loot.map(l => Object.assign({}, l)),
			deck: this.deck.cards.map(c => c.key),
			hand: this.hand.cards.map(c => c.key),
			court: this.court.cards.map(c => c.key),
			supply: this.supply.cards.map(c => c.key),
			discard: this.discard.cards.map(c => c.key),
			exile: this.exile.cards.map(c => c.key),
			rewarding: this.rewarding,
			choosingCard: this.choosingCard,
			options: this.options ? this.options.map(option => option.key) : null,
			turn: this.turn,
			turncount: this.turncount,
			mana: this.mana,
			turnmana: this.turnmana,
			enginesPlayed: this.enginesPlayed,
			variables: this.variables,
			overdecksize: this.overdecksize,
			stolenExp: this.stolenExp,
			done: this.done
		}
	}

	static build (run, src) {

		let fight = new Fight(run);
		fight.done = src.done;
		if (!src.done) {
			if (src.card) {
				fight.data.card = {};
				src.card.forEach(card => fight.data.card[card.key] = Card.build(fight, card));
			}
			if (src.enemy) {
				fight.data.enemy = {};
				src.enemy.forEach(enemy => fight.data.enemy[enemy.key] = Enemy.build(fight, enemy));
			}
			fight.enemies = (src.enemies || (src.enemy || []).map(enemy => enemy.key)).map(e => fight.find({type: "enemy", no: e})).filter(enemy => enemy);
			fight.slots = (src.slots || src.enemies.map(enemy => ({ enemy }))).map(slot => {
				let enemy = slot.enemy !== undefined ? fight.find({type: "enemy", no: slot.enemy}) : undefined;
				let output = {
					spawn: slot.spawn,
					scale: slot.scale,
					enemy
				};
				if (output.scale === undefined)
					output.scale = enemy ? fight.enemyModelScale(enemy) : 1;
				return output;
			});
			fight.enemies.forEach((enemy, index) => {
				const slotIndex = fight.slots.findIndex(slot => slot.enemy === enemy);
				enemy.slot = slotIndex === -1 ? index : slotIndex;
			});
			fight.deck.cards = src.deck.map(c => fight.find({type: "card", no: c}));
			fight.deck.cards.forEach(c => c.location = fight.deck);
			fight.hand.cards = src.hand.map(c => fight.find({type: "card", no: c}));
			fight.hand.cards.forEach(c => c.location = fight.hand);
			fight.court.cards = src.court.map(c => fight.find({type: "card", no: c}));
			fight.court.cards.forEach(c => c.location = fight.court);
			fight.supply.cards = src.supply.map(c => fight.find({type: "card", no: c}));
			fight.supply.cards.forEach(c => c.location = fight.supply);
			fight.discard.cards = src.discard.map(c => fight.find({type: "card", no: c}));
			fight.discard.cards.forEach(c => c.location = fight.discard);
			fight.exile.cards = src.exile.map(c => fight.find({type: "card", no: c}));
			fight.exile.cards.forEach(c => c.location = fight.exile);
		}
		fight.loot = src.loot.map(l => Object.assign({}, l));
		fight.player = Player.build(fight, src.player, !src.done);
		if (src.companion)
			fight.companion = Companion.build(fight, src.companion);
		fight.rewarding = src.rewarding;
		fight.choosingCard = src.choosingCard;
		fight.turn = src.turn;
		fight.turncount = src.turncount;
		fight.mana = src.mana;
		fight.turnmana = src.turnmana;
		fight.enginesPlayed = src.enginesPlayed || 0;
		fight.variables = src.variables;
		fight.overdecksize = src.overdecksize;
		fight.stolenExp = src.stolenExp;
		if (src.options)
			fight.options = src.options.map(option => Library.getCard(option));
		return fight;
	}
}
